import { useMutation } from "@tanstack/react-query";
import PropTypes from "prop-types";
import { useState } from "react";

import {
  NotificationsLevel,
  SubmitUrlModalDefaultMessages,
  UrlScraper
} from "../lib/enums";
import {
  useNetworkNavigation,
  useUrlDynamicAnalysisStatus
} from "../modules/network/hooks";
import { SUCCESS_STATUS as RLCS_SUCCESS_STATUS } from "../modules/network/hooks/useUrlDynamicAnalysisStatus";
import { INITIAL_RLCS_PARAMS } from "../modules/samples/components/Reanalyze";
import api from "../services/api";
import { route, settings } from "../shim/jsbridge";
import tcbase from "../shim/tcbase";
import ModalWrapper from "./ModalWrapper";
import SubmitUrlForm from "./SubmitUrlForm";
import useQuotaExceededForProduct, {
  PRODUCT_NAMES
} from "../hooks/useQuotaExceededForProduct";
import { QUOTA_EXCEEDED_MSG } from "../modules/samples/components/rlcloudsandbox/historytable/HistoryTitle";

const VALID_URL_PROTOCOL_REGEX = "^https?://.+";

const { notifications, consts: { cloud_enabled, t1000_enabled } = {} } =
  tcbase || {};

const { default_scraper: initialScraper } = settings || {};

const isTiCloudDisabled = !(cloud_enabled || t1000_enabled);

const INITIAL_VALUES = {
  url: "",
  scraper: initialScraper,
  rl_dynamic_analysis: false,
  ...INITIAL_RLCS_PARAMS
};

const chooseScraper = scraper =>
  scraper === UrlScraper.LOCAL || isTiCloudDisabled
    ? UrlScraper.LOCAL
    : UrlScraper.TI_CLOUD;

const getScraper = isRLDynamicAnalysis => {
  if (isRLDynamicAnalysis) return UrlScraper.TI_CLOUD;
  return chooseScraper(initialScraper);
};

/**
 * Prepend http:// to the url if it's missing
 */
export const prependHttpToUrl = url => {
  const isValidProtocol = url.match(VALID_URL_PROTOCOL_REGEX);
  return isValidProtocol ? url : `http://${url}`;
};

const handleDirtyFields = (previousValues, currentValues, isDirtyCallback) => {
  Object.keys(previousValues).forEach(formKey => {
    const prev = previousValues?.[formKey];
    const curr = currentValues?.[formKey] ?? prev;
    if (prev !== curr) {
      isDirtyCallback(formKey);
    }
  });
};

const getAdditionalInformation = (
  isRlcsQuotaExceeded,
  isRlcsQuotaExceededLoading
) => {
  if (isRlcsQuotaExceeded) {
    return QUOTA_EXCEEDED_MSG;
  } else if (isRlcsQuotaExceededLoading) {
    return "Loading...";
  } else {
    return undefined;
  }
};

const SubmitUrlModal = ({
  show,
  handleShow,
  url,
  initialValues = {},
  customMessages = {}
}) => {
  const [errors, setErrors] = useState({});
  const { navigateToUriPage } = useNetworkNavigation();

  const {
    isQuotaExceeded: isRlcsQuotaExceeded,
    isLoading: isRlcsQuotaExceededLoading
  } = useQuotaExceededForProduct(PRODUCT_NAMES.TCA_0207_URL_DYNAMIC_ANALYSIS);

  const clearErrors = errKey => {
    // clear specific errKey or clear all if not provided
    if (!errKey) setErrors({});
    else setErrors(({ [errKey]: _, ...rest }) => rest);
  };

  const [values, setValues] = useState({
    ...INITIAL_VALUES,
    ...{
      ...initialValues,
      url: url ?? "",
      scraper: getScraper(initialValues?.rl_dynamic_analysis)
    }
  });

  const notificationMessages = {
    ...SubmitUrlModalDefaultMessages,
    ...(customMessages || {})
  };

  const handleClose = () => {
    clearErrors();
    handleShow();
  };

  const handleValues = currValues => {
    setValues(prevValues => {
      handleDirtyFields(prevValues, currValues, clearErrors);
      return {
        ...prevValues,
        ...currValues
      };
    });
  };

  const query = useUrlDynamicAnalysisStatus(url);

  const { mutate: handleSubmit, isPending: isLoading } = useMutation({
    mutationFn: () =>
      api.uriTableFetchAndAnalyze(
        prependHttpToUrl(values.url),
        values.scraper,
        values.platform,
        values.geolocation,
        values.locale,
        values.interactive
      ),
    onSuccess: ({
      data: {
        url_analysis_id: urlAnalysisId,
        url_analysis_sha1: urlAnalysisSha1
      }
    }) => {
      notifications.show({
        level: NotificationsLevel.SUCCESS,
        html: true,
        message: notificationMessages.success
      });
      handleClose();
      navigateToUriPage(prependHttpToUrl(values.url));
      if (values.interactive) {
        if (urlAnalysisSha1 && urlAnalysisId) {
          window.open(
            route("rl_interactive_analysis_for_url", [
              encodeURIComponent(values.url),
              urlAnalysisSha1,
              urlAnalysisId
            ]),
            "_blank"
          );
        } else {
          notifications.show({
            level: NotificationsLevel.ERROR,
            message: notificationMessages.interactiveAnalysisError
          });
        }
      }
    },
    onError: error => {
      const { message, url: urlErr } = error.data || {};
      const errorMessage = urlErr?.[0] || message || "";
      setErrors(error.data);
      notifications.show({
        level: "error",
        message: errorMessage || notificationMessages.error
      });
    }
  });

  return (
    <ModalWrapper
      show={show}
      title={`Network Threat Intelligence`}
      size="small"
      onHide={() => {
        handleClose();
      }}
      data-testid="submit-url-modal"
    >
      <SubmitUrlForm
        values={values}
        errors={errors}
        onSubmit={() => {
          if (isLoading || !show) return;
          handleSubmit();
        }}
        setShow={() => {
          handleClose();
        }}
        onValues={val =>
          handleValues({
            url: val?.url?.trim(),
            scraper: val?.scraper,
            platform: val?.platform,
            geolocation: val?.geolocation,
            locale: val?.locale,
            rl_dynamic_analysis: val?.rl_dynamic_analysis,
            interactive: val?.interactive
          })
        }
        onValueChange={handleValues}
        isUrlDisabled={Boolean(url)}
        isTiCloudDisabled={isTiCloudDisabled}
        isRLDynamicDisabled={
          query.isSuccess &&
          !RLCS_SUCCESS_STATUS.includes(query.data?.analysis_status)
        }
        isSubmitButtonDisabled={isLoading || !show}
        rlcsAnalysisInfo={{
          enabled: !isRlcsQuotaExceeded && !isRlcsQuotaExceededLoading,
          additional_information: getAdditionalInformation(
            isRlcsQuotaExceeded,
            isRlcsQuotaExceededLoading
          )
        }}
      />
    </ModalWrapper>
  );
};

SubmitUrlModal.propTypes = {
  url: PropTypes.string,
  show: PropTypes.bool.isRequired,
  handleShow: PropTypes.func.isRequired,
  customMessages: PropTypes.shape({}),
  initialValues: PropTypes.shape({
    rl_dynamic_analysis: PropTypes.bool
  })
};

export default SubmitUrlModal;
